import { Component, Output, EventEmitter, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { FileService } from '../../services/file.service';

@Component({
  selector: 'app-file-uploader-input',
  templateUrl: './file-uploader-input.component.html',
  styleUrl: './file-uploader-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FileUploaderInputComponent {
  private fileService = inject(FileService);

  private static buildUploadResult(url: string, fileName: string, filePath: string, file: File) {
    return {
      url,
      fileName,
      filePath,
      size: file.size,
      mimeType: file.type,
    };
  }

  @Output() uploadComplete: EventEmitter<string> = new EventEmitter();
  @Output() uploadCompleteWithMetadata: EventEmitter<{
    url: string;
    fileName: string;
    filePath: string;
    size: number;
    mimeType: string;
  }> = new EventEmitter();
  @Output() fileSelected: EventEmitter<File> = new EventEmitter();
  @Output() uploadError: EventEmitter<string> = new EventEmitter();
  @Input() squareSize?: any;
  @Input() directory?: string;
  @Input() formId: string = 'fileUploaderInput';
  @Input() hidden: boolean = false;

  async onFileSelected(event: any) {
    let file: File = event.target.files[0];
    if (file) {
      // Emit that a file has been selected (upload starting)
      this.fileSelected.emit(file);

      if (this.squareSize) {
        file = await this.fileService.resizeImageToSquare(
          file,
          this.squareSize,
        );
      }

      const fileName = file.name;
      let filePath = `uploads/${fileName}`;

      if (this.directory) {
        filePath = `uploads/${this.directory}/${fileName}`;
      }

      // Try the original upload method first
      this.fileService.uploadFileAndGetMetadata(filePath, file).subscribe({
        next: (uploadData) => {
          uploadData.percentage.subscribe((progress) => {
            if (progress === 100) {
              uploadData.downloadURL.subscribe((url) => {
                if (url) {
                  this.uploadComplete.emit(url);
                  this.uploadCompleteWithMetadata.emit(
                    FileUploaderInputComponent.buildUploadResult(
                      url,
                      fileName,
                      filePath,
                      file,
                    ),
                  );
                }
              });
            }
          });
        },
        error: (error: any) => {
          console.warn(
            'Original upload method failed, trying alternative method:',
            error,
          );

          // Fallback to direct upload method if the original fails
          this.fileService.uploadFileDirectly(filePath, file).subscribe({
            next: (url) => {
              if (url) {
                this.uploadComplete.emit(url);
                this.uploadCompleteWithMetadata.emit(
                  FileUploaderInputComponent.buildUploadResult(
                    url,
                    fileName,
                    filePath,
                    file,
                  ),
                );
              }
            },
            error: (fallbackError) => {
              console.error('Both upload methods failed:', fallbackError);
              this.uploadError.emit('Failed to upload file. Please try again.');
            },
          });
        },
      });
    }
  }
}
